/*
 * Hat catalog — 9 hats, 5 default + 4 unlockable. Ported verbatim from v0.2.
 *
 * Indices are persistent; reordering breaks existing saves. To add a new hat,
 * append it to the end. (More hats are planned in TASK-04.)
 */

/*
 * Hat unlock conditions. Each variant has a `type` discriminator + a `value`
 * that the matching counter must meet or exceed.
 *
 * Single-run vs lifetime split:
 *   - `forage` / `fish` look at the score from a single run of that minigame
 *     (matches v0.2 behavior — "Forage 5 mushrooms in one go").
 *   - `lifetimeForage` / `lifetimeFish` look at the running total across all
 *     runs since the player first opened the game.
 *
 * Engagement counters (added 2026-05-17 for TASK-04's expanded catalog):
 *   - `chatCount` — every time the player picks a phrase from the say modal.
 *   - `playMinutes` — accumulated active-tab minutes since first run.
 *   - `consecutiveSessions` — distinct days the player has come back. Resets
 *     if more than 36 hours pass between sessions.
 */
export type HatUnlock =
  | { type: 'forage'; value: number }
  | { type: 'fish'; value: number }
  | { type: 'lifetimeForage'; value: number }
  | { type: 'lifetimeFish'; value: number }
  | { type: 'chatCount'; value: number }
  | { type: 'playMinutes'; value: number }
  | { type: 'consecutiveSessions'; value: number }
  | { type: 'stargaze'; value: number };

export interface Hat {
  /** Canonical name. Used as the unlock key in SaveData.unlocks. */
  name: string;
  /** null = always available; otherwise the threshold that unlocks it. */
  unlock: HatUnlock | null;
  /** Player-facing hint shown when they tap a locked hat. */
  hint?: string;
}

export const HATS: readonly Hat[] = [
  { name: 'none',     unlock: null },
  { name: 'acorn',    unlock: null },
  { name: 'mushroom', unlock: null },
  { name: 'leaf',     unlock: null },
  { name: 'star',     unlock: null },
  { name: 'flower',   unlock: { type: 'forage', value: 5 },  hint: 'Forage 5 mushrooms in one go' },
  { name: 'crown',    unlock: { type: 'forage', value: 12 }, hint: 'Forage 12 mushrooms in one go' },
  { name: 'bow',      unlock: { type: 'fish', value: 3 },    hint: 'Catch 3 fish in one trip' },
  { name: 'firefly',  unlock: { type: 'fish', value: 7 },    hint: 'Catch 7 fish in one trip' },
  // First of the TASK-04 spec'd hats to land. Catalog index is 9 (next available)
  // not 19 (the spec'd slot) — remaining new hats will claim 10..N in catalog order
  // as they're implemented. CONTENT_CATALOG.md tracks the mapping.
  { name: 'pinecone helm', unlock: { type: 'playMinutes', value: 30 }, hint: 'Spend 30 minutes in the Square' },
  // Index 10 — first hat unlocked via the stargaze minigame (TASK-08).
  { name: 'shooting star', unlock: { type: 'stargaze', value: 7 }, hint: 'Wish on 7 stars in one sitting' },
] as const;

/** Hats unlocked by default (the first 5). */
export const DEFAULT_UNLOCKS: readonly string[] = ['none', 'acorn', 'mushroom', 'leaf', 'star'];

/** Safe lookup; falls back to 'none' if index is out of range. */
export function hatAt(index: number): Hat {
  return HATS[index] ?? HATS[0]!;
}

/** Name lookup; used by drawHat dispatch. */
export function hatNameAt(index: number): string {
  return hatAt(index).name;
}
